import {
	type CSSProperties,
	type FC,
	type HTMLAttributes,
	type PropsWithChildren,
	type ReactNode,
	forwardRef,
} from 'react'
import BaseGridLayout from 'react-grid-layout'
import styled, { css } from 'styled-components'

import CrossIcon from 'assets/svg/app/close-bold.svg'
import { useAppDispatch } from 'state/hooks'

import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { setPreferences } from 'state/futures/reducer'
import { BREAKPOINTS } from 'styles/media'

const BaseResponsiveGridLayout = BaseGridLayout.WidthProvider(BaseGridLayout.Responsive)

type GridLayoutProps = {
	children: ReactNode
	rowHeight?: number
	margin?: [number, number]
	layouts?: BaseGridLayout.Layouts
}

export const DRAG_HANDLE_CLASS_NAME = 'drag-handle'
const NUMBER_OF_COLS = 48

export const GridLayout: FC<GridLayoutProps> = ({ children, rowHeight, margin, ...rest }) => {
	return (
		<StyledGridLayout className="layout" rowHeight={rowHeight} margin={margin} {...rest}>
			{children}
		</StyledGridLayout>
	)
}

export const ResponsiveGridLayout: FC<GridLayoutProps> = ({
	children,
	rowHeight = 6,
	margin = [3, 3],
	...rest
}) => {
	const dispatch = useAppDispatch()

	const handleOnLayoutChange = (
		_currentLayout: BaseGridLayout.Layout[],
		_allLayouts: BaseGridLayout.Layouts
	) => {
		dispatch(
			setPreferences({
				layouts: _allLayouts,
			})
		)
	}
	return (
		<StyledResponsiveGridLayout
			className="layout"
			autoSize={false}
			draggableHandle={`.${DRAG_HANDLE_CLASS_NAME}`}
			onLayoutChange={handleOnLayoutChange}
			breakpoints={BREAKPOINTS}
			cols={{
				xs: NUMBER_OF_COLS,
				sm: NUMBER_OF_COLS,
				md: NUMBER_OF_COLS,
				lg: NUMBER_OF_COLS,
				xl: NUMBER_OF_COLS,
				xxl: NUMBER_OF_COLS,
				xxxl: NUMBER_OF_COLS,
			}}
			rowHeight={rowHeight}
			margin={margin}
			{...rest}
		>
			{children}
		</StyledResponsiveGridLayout>
	)
}

interface GridItemProps extends HTMLAttributes<HTMLDivElement> {
	style?: CSSProperties
	className?: string
	onCloseClick?: () => void
	isStatic?: boolean
}

export const CustomGridItemComponent = forwardRef<HTMLDivElement, PropsWithChildren<GridItemProps>>(
	({ style, className, onCloseClick, children, isStatic, ...props }, ref) => {
		return (
			<GridItemContainer style={style} {...props} className={className} ref={ref}>
				{!isStatic && (
					<IconContainer className="close" onClick={onCloseClick}>
						<CrossIcon width={16} height={16} />
					</IconContainer>
				)}
				{children}
			</GridItemContainer>
		)
	}
)

const GridItemContainer = styled.div`
	border-radius: 4px;
	overflow: hidden;
`
const CSSLayout = css`
	overflow: auto;
	height: 100%;
	width: 100%;

	& .react-draggable.resizing iframe {
  	pointer-events: none !important;
	}

	& .react-draggable.react-draggable-dragging iframe {
  	pointer-events: none !important;
	}

	& .react-draggable .drag-handle {
		cursor: grab;
	}

	& .react-draggable.react-draggable-dragging .drag-handle {
		cursor: grabbing;
	}

	& .react-grid-item:hover > .react-resizable-handle::after {
		margin: 1px;
		width: 8px;
		height: 8px;
		border-right: 2px solid #575768;
		border-bottom: 2px solid #575768;
	}

	& .react-grid-item:hover .close {
		display: block;
	}

	& .react-grid-item {
		z-index: 2;
		background: ${({ theme }) => theme.colors.selectedTheme.newTheme.containers.primary.background} !important;
	}

	& .react-grid-placeholder {
		z-index: 1;
		background: rgb(212, 153, 0) !important;
		background: radial-gradient(
			circle,
			rgba(212, 153, 0, 1) 0%,
			rgba(255, 222, 0, 0.9948354341736695) 100%
		) !important;
		transition-duration: 0ms;
		opacity: 0.6;
	}

	& .react-draggable-dragging {
		z-index: 3;
		opacity: 0.9;
	}

	& .react-resizable-handle {
		z-index: 40000;
	}

	& .resizing {
		opacity: 0.9;
	}
`

const StyledResponsiveGridLayout = styled(BaseResponsiveGridLayout)`
	${CSSLayout}
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
`

const StyledGridLayout = styled(BaseGridLayout)`
	${CSSLayout}
`

const IconContainer = styled.div`
	position: absolute;
	display: none;
	z-index: 10;
	top: 0;
	right: 0;
	cursor: pointer;
	:hover {
		opacity: 0.5;
	}
`
